/**
 * File-type metadata and base64 attachment helpers, shared by any surface
 * that lists or previews uploaded documents.
 */

/** Visual + descriptive metadata for a file type. */
export interface FileTypeMeta {
  /** Short badge label shown on the glyph (e.g. `PDF`). */
  badge: string;
  /** Human-readable type name (e.g. `PDF Document`). */
  label: string;
  /** Accent colour for the glyph. */
  color: string;
}

interface FileTypeEntry extends FileTypeMeta {
  ext: string[];
}

// Extension-first lookup (the filename is the most reliable signal); the MIME
// subtype is the fallback when the name carries no extension.
const FILE_TYPES: FileTypeEntry[] = [
  { ext: ["pdf"], badge: "PDF", label: "PDF Document", color: "#ef4444" },
  { ext: ["xls", "xlsx"], badge: "XLS", label: "Excel Spreadsheet", color: "#16a34a" },
  { ext: ["csv"], badge: "CSV", label: "CSV File", color: "#16a34a" },
  { ext: ["doc", "docx"], badge: "DOC", label: "Word Document", color: "#2563eb" },
  {
    ext: ["ppt", "pptx"],
    badge: "PPT",
    label: "PowerPoint Presentation",
    color: "#ea580c",
  },
  {
    ext: ["zip", "rar", "7z", "gz", "tar"],
    badge: "ZIP",
    label: "ZIP Archive",
    color: "#9333ea",
  },
  {
    ext: ["png", "jpg", "jpeg", "gif", "webp", "svg"],
    badge: "IMG",
    label: "Image",
    color: "#0d9488",
  },
  { ext: ["txt", "log"], badge: "TXT", label: "Text File", color: "#64748b" },
  { ext: ["json", "xml", "yaml", "yml"], badge: "DATA", label: "Data File", color: "#0ea5e9" },
];

const extOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : "";
};

export const fileTypeMeta = (fileName: string, fileType: string): FileTypeMeta => {
  const ext = extOf(fileName);
  const subtype = fileType.split("/").pop()?.toLowerCase() ?? "";
  const match = FILE_TYPES.find((t) => t.ext.includes(ext) || t.ext.includes(subtype));
  if (match) {
    const { badge, label, color } = match;
    return { badge, label, color };
  }
  const fallbackBadge = (ext || subtype || "file").toUpperCase().slice(0, 4);
  return { badge: fallbackBadge, label: fileType || "File", color: "#64748b" };
};

export const isImage = (fileType: string): boolean =>
  fileType.toLowerCase().startsWith("image/");

export const isPdf = (fileType: string): boolean =>
  fileType.toLowerCase() === "application/pdf";

/** Only images and PDFs render inline; everything else is download-only. */
export const isPreviewable = (fileType: string): boolean =>
  isImage(fileType) || isPdf(fileType);

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/** The minimum an attachment needs to be previewed or downloaded. */
export interface PreviewableFile {
  fileName: string;
  fileType: string;
  fileSize: number;
  /** Base64-encoded contents, with no data-URI prefix. */
  fileData: string;
}

/** Build a data-URI from the base64 payload for inline preview / download. */
export const fileDataUrl = (file: PreviewableFile): string =>
  `data:${file.fileType};base64,${file.fileData}`;

/** Trigger a browser download of the file's base64 contents. */
export const downloadFile = (file: PreviewableFile): void => {
  const anchor = document.createElement("a");
  anchor.href = fileDataUrl(file);
  anchor.download = file.fileName || "attachment";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};
